import { get } from '@okta/okta-auth-js';
import type { OktaAuthHttpInterface } from '@okta/okta-auth-js';

import { rejectAfterTimeout } from './rejectAfterTimeout';

export const OKTA_SESSION_REQUEST_TIMEOUT_MS = 10000;

const OKTA_SESSION_PATH = '/api/v1/sessions/me';
const NO_SESSION_ERROR_CODE = 'E0000007';
const SESSIONS_API_DISABLED_ERROR_CODE = 'E0000022';

/**
 * - `active`: Okta answered with a live session.
 * - `inactive`: Okta answered that there is no session, so the user really is signed out of Okta.
 * - `ambiguous`: Okta gave no usable answer (network failure, timeout, rate limit, server error), so nothing is
 *   known about the session either way.
 */
export type OktaSessionOutcome = 'active' | 'inactive' | 'ambiguous';

export interface OktaSessionCheckResult {
    outcome: OktaSessionOutcome;
    /** Okta login of an active session. */
    login?: string;
    /** When an active session expires, as the ISO date string Okta returns. */
    expiresAt?: string;
    /** HTTP status of an ambiguous answer, when Okta answered at all. */
    httpStatus?: number;
}

interface OktaSessionResponse {
    status?: string;
    login?: string;
    expiresAt?: string;
}

interface OktaHttpError {
    errorCode?: string;
    xhr?: { status?: number };
}

/**
 * Asks Okta once for the session behind its session cookie, without retrying.
 *
 * `oktaAuth.session.get()` is not used because it turns every failure, including a network error or a rate-limit
 * response, into an `INACTIVE` session, which cannot be told apart from a real sign-out. This makes the same request
 * through the Okta SDK, but keeps the failure so it can be classified.
 */
export async function fetchOktaSession(oktaAuthInstance: OktaAuthHttpInterface): Promise<OktaSessionCheckResult> {
    try {
        const request = get(oktaAuthInstance, OKTA_SESSION_PATH, { withCredentials: true }) as Promise<
            OktaSessionResponse | null | undefined
        >;
        const session = await rejectAfterTimeout(request, OKTA_SESSION_REQUEST_TIMEOUT_MS, 'Okta session request');

        // An answer without a status says nothing about the session, so it must not sign the user out.
        if (typeof session?.status !== 'string') {
            return { outcome: 'ambiguous' };
        }

        if (session.status !== 'ACTIVE') {
            return { outcome: 'inactive' };
        }

        return { outcome: 'active', login: session.login, expiresAt: session.expiresAt };
    } catch (error) {
        return classifyFailedRequest(error);
    }
}

function classifyFailedRequest(error: unknown): OktaSessionCheckResult {
    const { errorCode, xhr } = (error ?? {}) as OktaHttpError;
    const httpStatus = xhr?.status;

    if (errorCode === SESSIONS_API_DISABLED_ERROR_CODE) {
        return { outcome: 'ambiguous', httpStatus };
    }

    if (httpStatus === 404 || errorCode === NO_SESSION_ERROR_CODE) {
        return { outcome: 'inactive' };
    }

    return { outcome: 'ambiguous', httpStatus };
}
